import { useEffect, useRef } from 'react';
import type { ParallaxPlan, ParallaxPose } from '../types';
import type { AnimationRegistry } from '../transitions';

/**
 * A photo rendered in 3D from its depth map.
 *
 * Each output pixel looks up where it came from: near parts of the photo
 * (bright in the depth map) are shifted and enlarged more than far parts, so
 * as the pose moves the scene separates into layers like a real camera move.
 * The source is found with a few fixed-point steps, which keeps edges clean at
 * these small amplitudes. Nothing is generated: every pixel is from the photo.
 *
 * Timing comes from a target-less Web Animation registered with the player's
 * AnimationRegistry, so pause/resume freezes the 3D move exactly like the
 * CSS-transform motion around it.
 */

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `
precision highp float;
uniform sampler2D uImg;
uniform sampler2D uDepth;
uniform vec2 uScale;    // canvas uv -> image uv
uniform vec2 uShift;    // sideways travel for the nearest point
uniform float uDolly;   // enlargement for the nearest point
uniform float uContain; // 1 = letterboxed: transparent outside the photo
varying vec2 vUv;
const float FOCUS = 0.3; // depth that stays put; nearer moves with, farther against
void main() {
  vec2 uv = (vUv - 0.5) * uScale + 0.5;
  if (uContain > 0.5 && (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0)) {
    gl_FragColor = vec4(0.0);
    return;
  }
  vec2 p = uv;
  for (int i = 0; i < 4; i++) {
    float rel = texture2D(uDepth, clamp(p, 0.0, 1.0)).r - FOCUS;
    p = uv - uShift * rel - (uv - 0.5) * uDolly * rel;
  }
  gl_FragColor = texture2D(uImg, clamp(p, 0.0005, 0.9995));
}`;

/** Extra zoom in cover mode so the shifted edges never reveal the border. */
const EDGE_MARGIN = 1.04;

interface Props {
  src: string;
  depthSrc: string;
  plan: ParallaxPlan;
  fit: 'cover' | 'contain';
  registry: AnimationRegistry;
  /** WebGL or a texture failed: the caller shows the flat photo instead. */
  onFallback: () => void;
  onAspect: (aspect: number) => void;
  label: string;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, source);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) ?? 'shader');
  return sh;
}

function texture(gl: WebGLRenderingContext, img: HTMLImageElement, mipmaps: boolean): WebGLTexture {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  if (mipmaps) {
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  } else {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  }
  return tex;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpPose = (a: ParallaxPose, b: ParallaxPose, t: number): ParallaxPose => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  dolly: lerp(a.dolly, b.dolly, t),
});

export function DepthPhoto({ src, depthSrc, plan, fit, registry, onFallback, onAspect, label }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Callbacks change identity on parent renders; the GL setup must not.
  const cb = useRef({ onFallback, onAspect });
  cb.current = { onFallback, onAspect };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;
    let raf = 0;
    let gl: WebGLRenderingContext | null = null;

    // The clock starts with the shot, before the images finish loading, so the
    // 3D move stays in step with the Ken Burns move around it.
    const clock = new Animation(new KeyframeEffect(null, [], { duration: plan.durationMs, fill: 'both' }));
    clock.play();
    registry.add(clock);

    const fail = (why: unknown) => {
      if (disposed) return;
      console.warn('[film] 3D photo fell back to flat:', why);
      cb.current.onFallback();
    };

    (async () => {
      const [img, depth] = await Promise.all([loadImage(src), loadImage(depthSrc)]);
      if (disposed) return;
      cb.current.onAspect(img.naturalWidth / img.naturalHeight);

      const gl2 = canvas.getContext('webgl2', { premultipliedAlpha: true, antialias: false, alpha: true });
      gl = (gl2 ?? canvas.getContext('webgl', { premultipliedAlpha: true, antialias: false, alpha: true })) as WebGLRenderingContext | null;
      if (!gl) throw new Error('WebGL unavailable');

      const prog = gl.createProgram()!;
      gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
      gl.useProgram(prog);

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const aPos = gl.getAttribLocation(prog, 'aPos');
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      gl.activeTexture(gl.TEXTURE0);
      texture(gl, img, Boolean(gl2));
      gl.activeTexture(gl.TEXTURE1);
      texture(gl, depth, false);
      gl.uniform1i(gl.getUniformLocation(prog, 'uImg'), 0);
      gl.uniform1i(gl.getUniformLocation(prog, 'uDepth'), 1);
      const uScale = gl.getUniformLocation(prog, 'uScale');
      const uShift = gl.getUniformLocation(prog, 'uShift');
      const uDolly = gl.getUniformLocation(prog, 'uDolly');
      gl.uniform1f(gl.getUniformLocation(prog, 'uContain'), fit === 'contain' ? 1 : 0);

      const imgAspect = img.naturalWidth / img.naturalHeight;
      let lastKey = '';

      const frame = () => {
        if (disposed || !gl) return;
        raf = requestAnimationFrame(frame);
        // Size the drawing buffer to the element (capped), only when it changes.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.min(2560, Math.round(canvas.clientWidth * dpr));
        const h = Math.min(2560, Math.round(canvas.clientHeight * dpr));
        if (!w || !h) return;
        const resized = canvas.width !== w || canvas.height !== h;
        if (resized) {
          canvas.width = w;
          canvas.height = h;
          gl.viewport(0, 0, w, h);
        }
        const t = Math.min(1, Math.max(0, Number(clock.currentTime ?? 0) / plan.durationMs));
        const key = `${t.toFixed(4)}|${w}x${h}`;
        if (key === lastKey) return; // paused: nothing to redraw
        lastKey = key;

        const canvasAspect = w / h;
        let sx: number, sy: number;
        if (fit === 'cover') {
          [sx, sy] = imgAspect > canvasAspect ? [canvasAspect / imgAspect, 1] : [1, imgAspect / canvasAspect];
          sx /= EDGE_MARGIN;
          sy /= EDGE_MARGIN;
        } else {
          [sx, sy] = imgAspect > canvasAspect ? [1, imgAspect / canvasAspect] : [canvasAspect / imgAspect, 1];
        }
        const pose = lerpPose(plan.from, plan.to, t);
        gl.uniform2f(uScale, sx, sy);
        gl.uniform2f(uShift, pose.x, pose.y);
        gl.uniform1f(uDolly, pose.dolly);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      frame();
    })().catch(fail);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      clock.cancel();
      // Free the GPU context now rather than waiting for garbage collection:
      // browsers cap live WebGL contexts.
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    };
    // A shot's plan and files never change while its layer is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas ref={canvasRef} role="img" aria-label={label} className="absolute inset-0 h-full w-full" />;
}
